import {
  languages,
  type Languages,
} from '../../../../i18n/i18n.language-config';
import type { IndustryValue } from '../../../../naics/LanguageIndustryAutoComplete';

/**
 * Whether a platform classifies industry by SIC rather than NAICS.
 *
 * <p>`LanguageIndustryAutoComplete` renders a different autocomplete for these
 * platforms; a single NAICS mapper writes the wrong code into `sicCode` and
 * drops the SIC descriptions entirely.
 */
export const usesSicClassification = (language: Languages): boolean =>
  language === languages['en-GB'] || language === languages['en-AU'];

/**
 * Module scope on purpose: `useIndustryCodeSearch` memoises on its mapper, so
 * an inline one would rebuild the search callback on every render.
 */
export const toNaicsOptions = (content: any[]): IndustryValue[] =>
  content.map((industry) => ({
    label: `${industry?.naicsCode}${
      industry?.secondaryIndustry ? ` [${industry.secondaryIndustry}]` : ''
    }`,
    value: industry?.naicsCode,
    meta: industry,
  }));

/**
 * Mirrors `SicIndustryAutoComplete`: rows with no SIC code are dropped, and the
 * whole row travels as `meta` so the descriptions can be written to the form.
 */
export const toSicOptions = (content: any[]): IndustryValue[] =>
  content.reduce((found: IndustryValue[], industry: any) => {
    if (industry?.sicCode) {
      found.push({
        label: `${industry.sicCode}${
          industry?.sicSecondaryDesc ? ` [${industry.sicSecondaryDesc}]` : ''
        }`,
        value: industry.sicCode,
        meta: industry,
      });
    }

    return found;
  }, []);
